"use client";

import { LoginForm } from '@/components/auth/AuthForm';
import { GuestAccessButton } from '@/components/auth/GuestAccessButton';
import { StaticAuthCard } from '@/components/auth/shared/StaticAuthCard';
import { LoginFooter } from '@/components/auth/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useCallback, useState, useEffect } from 'react';

// Simple hasMounted hook to avoid external dependency
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
}

export function LoginPageClient() {
  const { login, isLoggingIn, loginError, isAuthenticated, authInitialized } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGuestFallback, setShowGuestFallback] = useState(false);
  const hasMounted = useHasMounted();

  const handleLogin = useCallback(async (data: { email: string; password: string }) => {
    // Prevent double submission
    if (isSubmitting || isLoggingIn) {
      console.log('🚫 Login already in progress');
      return;
    }

    console.log('🚀 Login attempt initiated');
    
    try {
      setIsSubmitting(true);
      await login(data.email, data.password);
    } catch (error) {
      console.error('❌ Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [login, isLoggingIn, isSubmitting]);

  // Add timeout fallback for guest access - show after 5 seconds if auth not initialized
  useEffect(() => {
    if (hasMounted && !authInitialized && !isAuthenticated) {
      const timer = setTimeout(() => {
        console.log('⏰ Auth initialization timeout - showing guest access fallback');
        setShowGuestFallback(true);
      }, 5000); // 5 seconds timeout
      
      return () => clearTimeout(timer);
    }
  }, [hasMounted, authInitialized, isAuthenticated]);

  // Show guest access if auth is initialized OR after timeout fallback
  const showGuestAccess = hasMounted && !isAuthenticated && (authInitialized || showGuestFallback);

  return (
    <StaticAuthCard
      title="Welcome"
      description="Enter your credentials to access your account"
    >
      <div className="space-y-4">
        <LoginForm 
          onSubmit={handleLogin}
          isLoading={isLoggingIn || isSubmitting}
          error={loginError}
        />
        
        {/* Show loading state while auth is initializing */}
        {hasMounted && !authInitialized && !showGuestFallback && (
          <div className="flex items-center justify-center py-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            <span className="text-sm text-muted-foreground">
              {isAuthenticated ? 'Loading...' : 'Preparing guest access...'}
            </span>
          </div>
        )}
        
        {showGuestAccess && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or
                </span>
              </div>
            </div>
            
            <GuestAccessButton 
              variant="outline"
              className="w-full"
            />
          </>
        )}
      </div>
      
      <div className="mt-4 text-center">
        <LoginFooter />
      </div>
    </StaticAuthCard>
  );
} 